// Editorial insights & whitepapers. Body is an array of typed blocks
// so the article renderer stays declarative.

export const INSIGHTS = [
    {
        slug: "rbi-climate-risk-framework-briefing",
        type: "Regulator Briefing",
        chapter: "Vol. 02 · Issue 01",
        category: "Supervisory Guidance",
        title: "The RBI Draft Framework on Climate Risk — a supervisory read-through",
        subtitle:
            "What the Reserve Bank of India's climate-risk disclosure guidance actually asks of banks — and where the gap sits between policy intent and portfolio reality.",
        date: "2026-06-24",
        readMinutes: 12,
        authors: [
            { name: "The Carbonis Research Desk", role: "Climate Risk Infrastructure" }
        ],
        hero:
            "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBib2FyZHJvb218ZW58MHx8fHwxNzg0MzU5ODY3fDA&ixlib=rb-4.1.0&q=85",
        summary:
            "The RBI's Draft Disclosure Framework on Climate-Related Financial Risks is not another disclosure template. It is a supervisory-review instrument. This briefing decodes what a board-approved climate risk strategy actually looks like when a JPC walks in — and why most Indian banks are not yet 12 months from being ready.",
        body: [
            { type: "chapter", n: "01", label: "What the framework asks" },
            { type: "h2", text: "Four asks, one supervisory intent" },
            {
                type: "p",
                text:
                    "The RBI's draft framework rests on four pillars: governance, strategy, risk management, and metrics & targets. Each pillar is written in TCFD grammar — but read as supervisory ammunition. A board that cannot describe how its climate risk appetite is set, calibrated, and challenged is a board that will not survive the first thematic review."
            },
            {
                type: "pullquote",
                text:
                    "The framework does not ask you to have low emissions. It asks you to have a defensible answer for how your portfolio behaves when other people don't."
            },
            { type: "chapter", n: "02", label: "Where the gap sits" },
            { type: "h2", text: "Three questions most Indian banks cannot yet answer" },
            {
                type: "list",
                items: [
                    "What is the financed-emission attribution of your top 25 borrower groups, using a PCAF-compliant methodology, refreshed quarterly?",
                    "What is your CBAM-exposed portfolio? Not sector-tagged — verified, at the borrower level, with a documented exposure heatmap?",
                    "How does your ICAAP treat a 40-90 bps COC uplift on high-intensity corporates, and what is the scenario governance around that assumption?"
                ]
            },
            {
                type: "stats",
                items: [
                    { v: "24 months", k: "typical remediation window from draft to enforceable framework" },
                    { v: "3 layers", k: "board · risk committee · supervisor — must give consistent answers" },
                    { v: "PCAF v2", k: "the financed-emission methodology now supervisor-tracked" },
                    { v: "40–90 bps", k: "COC uplift already modelled by leading Indian PSBs on high-intensity borrowers" }
                ]
            },
            { type: "chapter", n: "03", label: "What Carbonis does" },
            { type: "h2", text: "The read-only overlay for supervisory readiness" },
            {
                type: "p",
                text:
                    "Carbonis operates as a read-only intelligence layer above your core banking systems. It ingests borrower-level exposure, applies PCAF-aligned attribution, produces a portfolio-level heatmap, and exports supervisor-grade dossiers on demand. No data migration. No integration. No workflow disruption. The bank retains its systems of record; Carbonis produces the answer the framework asks for."
            }
        ]
    },
    {
        slug: "msme-climate-playbook",
        type: "Playbook",
        chapter: "Vol. 02 · Issue 02",
        category: "MSME Field Guide",
        title: "The MSME Climate Playbook — from first inventory to first delisted competitor",
        subtitle:
            "A practical field guide for Indian MSME founders and CFOs preparing for CBAM, buyer Scope 3 KPIs, and the coming credit-risk repricing.",
        date: "2026-07-11",
        readMinutes: 10,
        authors: [
            { name: "The Carbonis Research Desk", role: "Climate Risk Infrastructure" }
        ],
        hero:
            "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1800&q=80",
        summary:
            "Most MSME climate advice is written for someone else. This playbook is written for the CFO of a mid-tier auto components exporter, a textile mill in Coimbatore, or a chemicals SME in Vapi — the businesses that anchor 70% of Indian exports and receive 3% of the climate-risk conversation.",
        body: [
            { type: "chapter", n: "01", label: "Where to start" },
            { type: "h2", text: "The 30-day baseline" },
            {
                type: "p",
                text:
                    "You do not need a consulting engagement to start. You need last year's utility bills, fuel invoices, three supplier POs, and a laptop. In 30 days, a competent CFO with the right structure can produce a GHG-Protocol-aligned Scope 1 and 2 inventory, a working Scope 3 estimate for the top five upstream and downstream categories, and a first-pass CBAM dossier for any European-bound product."
            },
            {
                type: "list",
                items: [
                    "Week 1 · Boundary setting. What entities, what geographies, what reporting year.",
                    "Week 2 · Scope 1 and 2 data pull. Fuel receipts, DISCOM bills, captive genset logs.",
                    "Week 3 · Priority Scope 3. Top five suppliers, top three logistics partners, downstream distribution.",
                    "Week 4 · CBAM dossier draft. Product-level emissions, verification-ready formatting."
                ]
            },
            {
                type: "pullquote",
                text:
                    "The MSME that measures first, wins the buyer. The MSME that measures third, loses the order."
            },
            { type: "chapter", n: "02", label: "What actually reduces" },
            { type: "h2", text: "Where the abatement lives" },
            {
                type: "p",
                text:
                    "The abatement cost curve for a mid-tier Indian MSME is shallower than it is for a listed conglomerate. Load management, thermal recovery, and fuel-switching typically deliver 20–40% reductions at negative or marginal cost. Solar PPAs, structured around the MSME's cash-flow calendar (not the buyer's ESG report deadline), can add another 15–25%. The trap is not the cost. It is the sequencing."
            },
            {
                type: "stats",
                items: [
                    { v: "30 days", k: "to a Scope 1+2 inventory that survives a buyer audit" },
                    { v: "20–40%", k: "abatement potential at negative or marginal cost, MSME median" },
                    { v: "€0.34/kg", k: "CBAM tariff on unverified hot-rolled steel exports, 2026" },
                    { v: "1 quarter", k: "typical delisting cycle after buyer flags Scope 3 non-compliance" }
                ]
            },
            { type: "chapter", n: "03", label: "How Carbonis fits" },
            { type: "h2", text: "The overlay, not another consultant" },
            {
                type: "p",
                text:
                    "Carbonis is designed for the reality of an Indian MSME — data-light, cash-flow-sensitive, and time-poor. We produce baseline inventories in weeks, not quarters; a confidence-weighted Carbonis Score that buyers and lenders can read; and an abatement roadmap ranked by marginal cost, not by consultant preference. The score is portable — one number, one dossier — accepted by buyers, banks, and the frameworks that matter."
            }
        ]
    },
    {
        slug: "scope-3-systemic-financial-risk",
        type: "Whitepaper",
        chapter: "Vol. 01 · Issue 01",
        category: "Systemic Risk",
        title: "Scope 3 Emissions as a Systemic Financial Risk",
        subtitle:
            "Why the next financial crisis may be hidden in value chains — a whitepaper by Carbonis for MSMEs, banks, and regulators.",
        date: "2026-11-14",
        readMinutes: 18,
        authors: [
            { name: "The Carbonis Research Desk", role: "Climate Risk Infrastructure" }
        ],
        hero:
            "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=1800&q=80",
        summary:
            "Scope 3 is not a reporting inconvenience. It is a hidden concentration of counterparty, sovereign, and transition risk sitting inside every commercial loan book, every export contract, and every regulated portfolio in India. This whitepaper argues that Scope 3 will behave, in the next decade, the way sub-prime mortgages behaved in the 2000s: opaque, un-priced, and reflexively linked to solvency.",
        body: [
            { type: "chapter", n: "01", label: "The Argument" },
            { type: "h2", text: "The invisible layer of the balance sheet" },
            {
                type: "p",
                text:
                    "For most Indian mid-market firms, Scope 3 emissions are between 6× and 11× the size of Scope 1 and 2 combined. They sit in raw materials, upstream logistics, third-party manufacturing, and downstream distribution — pockets no financial statement has ever been designed to expose. This is the layer the CBAM, the SBTi, and the RBI's supervisory guidance are now converging on."
            },
            {
                type: "pullquote",
                text:
                    "The 2008 crisis was hidden in tranches nobody had priced. The 2030 crisis, if it arrives, will be hidden in emissions nobody has attributed."
            },
            {
                type: "p",
                text:
                    "The problem is not that Scope 3 emissions are large. The problem is that they are reflexive: a bank's climate risk is a function of its borrowers' Scope 3, which is a function of their suppliers' Scope 3, which is a function of a chain the bank cannot see, cannot audit, and — under current tooling — cannot price."
            },
            { type: "chapter", n: "02", label: "The Mechanics" },
            { type: "h2", text: "How Scope 3 becomes credit loss" },
            {
                type: "p",
                text:
                    "Consider a mid-sized Indian auto components exporter carrying a ₹180 Cr term loan. Its Scope 1 and 2 footprint is well-lit — captive genset, grid electricity, in-plant fuel. Its Scope 3, however, includes steel from three vendors, aluminium from two, and downstream freight through a fourth. The moment one of those vendors is delisted by a European OEM under CBAM's phase-2 documentation rules — a scenario now scheduled for 2027 — the exporter's order book contracts within a single quarter. The loan does not default because of climate. It defaults because a downstream buyer refused to place an order."
            },
            {
                type: "stats",
                items: [
                    { v: "6–11×", k: "Scope 3 as multiple of Scope 1+2 for Indian MSMEs" },
                    { v: "€85 / t", k: "CBAM default price, 2026 onwards" },
                    { v: "68%", k: "EU OEMs with active Scope 3 supplier KPIs" },
                    { v: "1 quarter", k: "Typical order-book contraction after delisting" }
                ]
            },
            {
                type: "p",
                text:
                    "In this scenario, the bank has taken a credit loss on an event that never appeared in its ICAAP, its stress test, or its portfolio dashboard. The exposure was there — measurable — from the beginning. Nobody was measuring."
            },
            { type: "chapter", n: "03", label: "The Response" },
            { type: "h2", text: "What has to change" },
            {
                type: "list",
                items: [
                    "Scope 3 must be treated as a Basel-relevant risk category, not a disclosure line. This means capital, not communications.",
                    "MSME onboarding for banks must include upstream and downstream mapping, not just borrower-level footprints. A carbon-blind KYC is now a carbon-blind risk assessment.",
                    "Regulators must define materiality thresholds for financed Scope 3 that are enforceable in supervisory review, not aspirational in taxonomy documents.",
                    "Buyers must publish supplier delisting criteria in advance, so financing institutions can price transition risk before it hits."
                ]
            },
            {
                type: "p",
                text:
                    "Carbonis's position is deliberately narrow. We do not argue for more disclosure. We argue for pricing. Every un-priced tonne of Scope 3 is a subsidy — from lenders to borrowers, from India to the EU, from the future to the present. Ending that subsidy is the shortest path to a financial system that can absorb a carbon-priced world."
            }
        ]
    },
    {
        slug: "reducing-scope-3-sustainable-future",
        type: "Whitepaper",
        chapter: "Vol. 01 · Issue 02",
        category: "Mitigation",
        title: "Reducing Scope 3 Emissions",
        subtitle:
            "The overlooked key to achieving a sustainable future — where MSME abatement, buyer engagement, and financed emissions converge.",
        date: "2026-11-28",
        readMinutes: 14,
        authors: [
            { name: "The Carbonis Research Desk", role: "Climate Risk Infrastructure" }
        ],
        hero:
            "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1800&q=80",
        summary:
            "Scope 3 is the largest emissions category in almost every industrial economy — and the least addressed. This whitepaper maps the shortest path from measurement to reduction, and argues that MSMEs, not their large-corporate buyers, will drive the majority of real abatement over the next decade.",
        body: [
            { type: "chapter", n: "01", label: "The Gap" },
            { type: "h2", text: "The 70% that nobody is reducing" },
            {
                type: "p",
                text:
                    "For the median Indian industrial supply chain, Scope 3 emissions represent between 65% and 85% of total footprint. Yet corporate climate strategy — even at the most ambitious buyers — remains focused on the 15–35% that lives inside Scope 1 and 2. This is not because Scope 3 is difficult. It is because the incentives to act on it are misaligned. The buyer sees a KPI, the supplier sees a compliance cost, the lender sees a risk it cannot underwrite."
            },
            {
                type: "pullquote",
                text:
                    "You cannot decarbonise a value chain by asking the largest company in it to try harder. You have to make the smallest ones capable."
            },
            { type: "chapter", n: "02", label: "Who moves first" },
            { type: "h2", text: "MSMEs will drive the majority of abatement" },
            {
                type: "p",
                text:
                    "This is the counter-intuitive finding. The abatement cost curve for a large listed company is steep — solar PPAs and efficiency retrofits are quickly exhausted, leaving only expensive and speculative options. For an MSME, in contrast, the curve is shallow: fuel-switching, thermal recovery, load management, and modest process changes can deliver 20–40% reductions at negative or marginal cost. What is missing is not the abatement. It is the capital and the mandate."
            },
            {
                type: "stats",
                items: [
                    { v: "65–85%", k: "Scope 3 share of total footprint" },
                    { v: "20–40%", k: "MSME abatement potential at negative-to-marginal cost" },
                    { v: "3 years", k: "Typical MACC payback for MSME efficiency" },
                    { v: "0.55 pts", k: "Composite risk score reduction per 1% Scope 3 cut (Carbonis model)" }
                ]
            },
            { type: "chapter", n: "03", label: "The Instrument Stack" },
            { type: "h2", text: "Three instruments, one path" },
            {
                type: "list",
                items: [
                    "Operational efficiency ranked by marginal abatement cost. Not a spreadsheet. A ranked, funded, tracked plan.",
                    "Renewable procurement structured around the MSME's cash-flow calendar, not the buyer's ESG report deadline.",
                    "Vetted removals and compliance offsets to neutralise the residual — used sparingly, and only after abatement is exhausted."
                ]
            },
            {
                type: "p",
                text:
                    "Reducing Scope 3 is not a communications strategy. It is a capital allocation problem, an information problem, and — for the MSMEs that anchor Indian exports — a survival problem. The businesses that solve it in this decade will hold the pricing power in the next."
            }
        ]
    },
    {
        slug: "cbam-2026-indian-steel-exporters",
        type: "Dispatch",
        chapter: "Field Note",
        category: "CBAM",
        title: "CBAM's 2026 default price and what it will really cost Indian steel exporters",
        subtitle:
            "€85 / tCO₂e. A €0.34 – €0.48 per-kg tariff. And an order book that is quietly re-writing itself in Rotterdam.",
        date: "2026-12-06",
        readMinutes: 6,
        authors: [
            { name: "The Carbonis Research Desk", role: "Climate Risk Infrastructure" }
        ],
        hero:
            "https://images.pexels.com/photos/355935/pexels-photo-355935.jpeg",
        summary:
            "The CBAM default emissions value for hot-rolled steel is high enough that, at €85 / tCO₂e, it exceeds the operating margin of the average Indian exporter. This is not a 2030 concern. It is a Q1-2026 concern.",
        body: [
            { type: "chapter", n: "—", label: "Field Note" },
            { type: "h2", text: "The number that matters" },
            {
                type: "p",
                text:
                    "The CBAM 2026 default emissions factor for hot-rolled steel sits between 2.0 and 2.8 tCO₂e per tonne of product. At the transitional-period default price of €85 / tCO₂e, this converts to a per-kilogram tariff of €0.17 to €0.24 — before verified data is submitted, before offsets, before any bilateral relief. For a mid-tier Indian mill running on 3–5% operating margin, that tariff exceeds the margin on the export order itself."
            },
            {
                type: "pullquote",
                text:
                    "The question is no longer whether Indian steel exports absorb the CBAM. It is which mills will still be exporting when they do."
            },
            {
                type: "p",
                text:
                    "The mitigating path is measurement. A verified emissions dossier that is 30% below the default gets the tariff below the operating margin. A dossier 50% below turns CBAM from a threat into a competitive moat against unmeasured competitors. The window to prepare that dossier — data, verification, submission — is now single-digit weeks."
            }
        ]
    }
];

export function getInsight(slug) {
    return INSIGHTS.find((i) => i.slug === slug);
}

export function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}
